import React from "react";
import { RouteObject, redirect } from "react-router";

const ProductEditorRoutes: RouteObject[] = [
  {
    path: "detail",
    async lazy() {
      const { Details } = await import(
        /* webpackChunkName: "orgs-cache-product-editor-tabs-details" */ "orgs/Configuration/ProductsTab/PriceListEditor/ProductEditor/Tabs/Details"
      );
      return {
        element: <Details />,
      };
    },
  },
  {
    path: "documents",
    async lazy() {
      const { Documents } = await import(
        /* webpackChunkName: "orgs-cache-product-editor-tabs-documents" */ "orgs/Configuration/ProductsTab/PriceListEditor/ProductEditor/Tabs/Documents"
      );
      return {
        element: <Documents />,
      };
    },
  },
  {
    path: "photos",
    async lazy() {
      const { Photos } = await import(
        /* webpackChunkName: "orgs-cache-product-editor-tabs-photos" */ "orgs/Configuration/ProductsTab/PriceListEditor/ProductEditor/Tabs/Photos"
      );
      return {
        element: <Photos />,
      };
    },
  },
  {
    path: "sizes",
    async lazy() {
      const { Sizes } = await import(
        /* webpackChunkName: "orgs-cache-product-editor-tabs-sizes" */ "orgs/Configuration/ProductsTab/PriceListEditor/ProductEditor/Tabs/Sizes"
      );
      return {
        element: <Sizes />,
      };
    },
  },
  {
    path: "option_groups",
    async lazy() {
      const { OptionGroups } = await import(
        /* webpackChunkName: "orgs-cache-product-editor-tabs-option-groups" */ "orgs/Configuration/ProductsTab/PriceListEditor/ProductEditor/Tabs/OptionGroups"
      );
      return {
        element: <OptionGroups />,
      };
    },
    children: [
      {
        path: "edit_option/:optionId",
        async lazy() {
          const { EditForm } =
            await import("orgs/Configuration/ProductsTab/PriceListEditor/ProductEditor/Tabs/OptionGroups/OptionGroupsTable/OptionTable/EditForm");
          return {
            element: <EditForm />,
          };
        },
      },
    ],
  },
  {
    path: "",
    loader: ({ request }) => {
      const url = new URL(request.url);
      return redirect(`detail${url.search}`);
    },
  },
];

export const ProductRoutes: RouteObject[] = [
  {
    path: "",
    async lazy() {
      const { ProductsTab } = await import(
        /* webpackChunkName: "orgs-cache-products-tab" */ "orgs/Configuration/ProductsTab"
      );
      return {
        element: <ProductsTab />,
      };
    },
    children: [
      {
        path: "",
        async lazy() {
          const { PriceList } = await import(
            /* webpackChunkName: "orgs-cache-price-list" */ "orgs/Configuration/ProductsTab/PriceList"
          );
          return {
            element: <PriceList />,
          };
        },
        async loader(args) {
          const { priceListsLoader } = await import(
            /* webpackChunkName: "orgs-cache-price-lists-loader" */ "src/router/loaders/orgs/Products"
          );
          return priceListsLoader(args);
        },
      },
      {
        path: "inspection",
        async lazy() {
          const { PriceList } = await import(
            /* webpackChunkName: "orgs-cache-price-list" */ "orgs/Configuration/ProductsTab/PriceList"
          );
          return {
            element: <PriceList />,
          };
        },
        async loader(args) {
          const { inspectionPriceListsLoader } = await import(
            /* webpackChunkName: "orgs-cache-inspection-price-lists-loader" */ "src/router/loaders/orgs/Products"
          );
          return inspectionPriceListsLoader(args);
        },
      },
      {
        path: "editor",
        shouldRevalidate: () => false,
        async loader(args) {
          const { productsEditorScreenLoader } = await import(
            /* webpackChunkName: "orgs-cache-products-editor-screen-loader" */ "src/router/loaders/orgs/Products"
          );
          return productsEditorScreenLoader(args);
        },
        children: [
          {
            path: ":priceListId",
            shouldRevalidate: ({ currentUrl, nextUrl }) =>
              !currentUrl.pathname.includes("choose_parent") && !nextUrl.pathname.includes("choose_parent"),
            async loader(args) {
              const { productsEditorLoader } = await import(
                /* webpackChunkName: "orgs-cache-products-editor-loader" */ "src/router/loaders/orgs/Products"
              );
              return productsEditorLoader(args);
            },
            async lazy() {
              const { PriceListEditor } = await import(
                /* webpackChunkName: "orgs-cache-price-list-editor" */ "orgs/Configuration/ProductsTab/PriceListEditor"
              );
              return {
                element: <PriceListEditor />,
              };
            },
            children: [
              {
                path: "choose_parent/:productId",
                async loader(args) {
                  const { chooseParentLoader } = await import(
                    /* webpackChunkName: "orgs-cache-price-list-editor" */ "src/router/loaders/orgs/Products"
                  );
                  return chooseParentLoader(args);
                },
                async lazy() {
                  const { ChooseParentModal } =
                    await import("orgs/Configuration/ProductsTab/PriceListEditor/ProductEditor/ProductTable/ProductTableRow/ChooseParentModal");
                  return {
                    element: <ChooseParentModal />,
                  };
                },
              },
            ],
          },
        ],
      },
      {
        path: "links",
        shouldRevalidate: () => false,
        async loader(args) {
          const { measurementLinksLoader } = await import("src/router/loaders/orgs/Products");
          return measurementLinksLoader(args);
        },
        async lazy() {
          const { MeasurementLinks } = await import("orgs/Configuration/ProductsTab/MeasurementLinks");
          return {
            element: <MeasurementLinks />,
          };
        },
      },
      {
        path: "validations",
        async lazy() {
          const { Validations } = await import(
            /* webpackChunkName: "orgs-cache-validations" */ "orgs/Configuration/ProductsTab/Validations"
          );
          return {
            element: <Validations />,
          };
        },
        async loader(args) {
          const { validationsLoader } = await import(
            /* webpackChunkName: "orgs-cache-validations-loader" */ "src/router/loaders/orgs/Products"
          );
          return validationsLoader(args);
        },
      },
      {
        path: "estimate_templates",
        async lazy() {
          const { EstimateTemplates } = await import(
            /* webpackChunkName: "orgs-cache-estimate-templates" */ "orgs/Configuration/ProductsTab/EstimateTemplates"
          );
          return {
            element: <EstimateTemplates />,
          };
        },
      },
    ],
  },
  {
    path: "price_list/:priceListId",
    async loader(args) {
      const { productsSummaryLoader } = await import(
        /* webpackChunkName: "orgs-cache-products-summary-loader" */ "src/router/loaders/orgs/Products"
      );
      return productsSummaryLoader(args);
    },
    async lazy() {
      const { ProductEditor } = await import(
        /* webpackChunkName: "orgs-cache-product-editor" */ "orgs/Configuration/ProductsTab/PriceListEditor/ProductEditor"
      );
      return {
        element: <ProductEditor />,
      };
    },
    children: [
      {
        path: "edit_product",
        shouldRevalidate: () => false,
        async loader(args) {
          const { loadProduct } = await import(
            /* webpackChunkName: "orgs-cache-load-product" */ "src/router/loaders/orgs/Products"
          );
          return loadProduct(args);
        },
        async lazy() {
          const { ProductEditorTabs } = await import(
            /* webpackChunkName: "orgs-cache-product-editor-tabs" */ "orgs/Configuration/ProductsTab/PriceListEditor/ProductEditor/Tabs"
          );
          return {
            element: <ProductEditorTabs />,
          };
        },

        children: ProductEditorRoutes,
      },
      {
        path: "add_product",
        async lazy() {
          const { ProductEditorTabs } = await import(
            /* webpackChunkName: "orgs-cache-product-editor-tabs" */ "orgs/Configuration/ProductsTab/PriceListEditor/ProductEditor/Tabs"
          );
          return {
            element: <ProductEditorTabs />,
          };
        },
        children: ProductEditorRoutes,
      },
      {
        path: "add_category",
      },
    ],
  },
  {
    path: "*",
    loader: () => {
      return redirect("");
    },
  },
];
